import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Empty, Segmented, Space, Spin, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { getAppointmentsByOrganization } from '../store/user/thunks';
import { getMedicalRecordOrganizationOrderAttentions } from '../store/personalrecord/thunks';
import styles from './AppointmentManagement.module.scss';

const { Text, Title } = Typography;

type AnyRecord = Record<string, any>;

interface WorkflowAppointment {
    id?: string;
    dni?: string;
    name?: string;
    email?: string;
    phone?: string;
    userName?: string;
    specialityName?: string;
    centerName?: string;
    date?: string;
    hour?: string;
    status?: string;
    type?: string;
    serviceType?: string;
    origin?: string;
}

interface WorkflowManagementProps {
    getAppointmentsByOrganization?: any;
    getMedicalRecordOrganizationOrderAttentions?: any;
    organizationId?: string;
    organizationName?: string;
    isClinic?: boolean;
}

const valueOf = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key];
    }
    return undefined;
};

const normalizeAppointments = (source: AnyRecord | AnyRecord[] | null | undefined): WorkflowAppointment[] => {
    const values = Array.isArray(source) ? source : valueOf(source, 'appointments', 'Appointments');
    return Array.isArray(values) ? values.map((item: AnyRecord) => ({
        id: valueOf(item, 'id', 'Id'),
        dni: valueOf(item, 'dni', 'Dni'),
        name: valueOf(item, 'name', 'Name'),
        email: valueOf(item, 'email', 'Email'),
        phone: valueOf(item, 'phone', 'Phone'),
        userName: valueOf(item, 'userName', 'UserName'),
        specialityName: valueOf(item, 'specialityName', 'SpecialityName'),
        centerName: valueOf(item, 'centerName', 'CenterName'),
        date: valueOf(item, 'date', 'Date'),
        hour: valueOf(item, 'hour', 'Hour'),
        status: valueOf(item, 'status', 'Status'),
        type: valueOf(item, 'type', 'Type'),
        serviceType: valueOf(item, 'serviceType', 'ServiceType'),
    })) : [];
};

const normalizeOrderAttentions = (source: AnyRecord | AnyRecord[] | null | undefined): WorkflowAppointment[] => {
    const values = Array.isArray(source) ? source : valueOf(source, 'orderAttentions', 'OrderAttentions', 'items', 'Items');
    return Array.isArray(values) ? values.map((item: AnyRecord) => {
        const appointmentDate = valueOf(item, 'appointmentDate', 'AppointmentDate');
        const createdAt = valueOf(item, 'orderAttentionCreatedAt', 'OrderAttentionCreatedAt');
        const appointmentMoment = appointmentDate && dayjs(appointmentDate).isValid() ? dayjs(appointmentDate) : null;
        return {
            id: valueOf(item, 'orderAttentionId', 'OrderAttentionId', 'id', 'Id'),
            dni: valueOf(item, 'personalRecordDni', 'PersonalRecordDni'),
            name: valueOf(item, 'personalRecordFullName', 'PersonalRecordFullName'),
            userName: valueOf(item, 'doctorName', 'DoctorName'),
            specialityName: valueOf(item, 'specialty', 'Specialty'),
            date: appointmentMoment?.format('YYYY-MM-DD') || createdAt,
            hour: appointmentMoment?.format('HH:mm:ss'),
            status: valueOf(item, 'timeStatus', 'TimeStatus'),
            type: 'ATENCIÓN DIRECTA',
            serviceType: 'Orden de atención',
            origin: 'Apertura directa',
        };
    }) : [];
};

const appointmentStatusGroup = (status?: string) => {
    const normalized = String(status || '').toUpperCase();
    if (normalized.includes('CONSULT') || normalized.includes('SALA')) return 'IN_CONSULTATION';
    if (normalized.includes('ATEND') || normalized.includes('COMPLET')) return 'ATTENDED';
    return 'PENDING';
};

const appointmentStatusLabel = (status?: string) => {
    const group = appointmentStatusGroup(status);
    if (group === 'IN_CONSULTATION') return 'En consulta';
    if (group === 'ATTENDED') return 'Atendido';
    return 'Pendiente';
};

const appointmentHour = (hour?: string) => (hour || '').slice(0, 5);

const appointmentDateLabel = (date?: string) => {
    if (!date) return 'Fecha no registrada';

    const normalized = date.trim();
    const slashDate = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDate) {
        const [, month, day, year] = slashDate;
        const parsed = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : normalized;
    }

    const parsed = dayjs(normalized);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : normalized;
};

const WorkflowManagement: React.FC<WorkflowManagementProps> = ({
    getAppointmentsByOrganization: loadAppointments,
    getMedicalRecordOrganizationOrderAttentions: loadOrderAttentions,
    organizationId,
    organizationName,
    isClinic = false,
}) => {
    const [appointments, setAppointments] = useState<WorkflowAppointment[]>([]);
    const [selectedAppointment, setSelectedAppointment] = useState<WorkflowAppointment | null>(null);
    const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'IN_CONSULTATION' | 'ATTENDED'>('ALL');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const today = dayjs();
                const [appointmentResponse, orderResponse] = await Promise.all([
                    loadAppointments?.(organizationId),
                    loadOrderAttentions?.(today.startOf('day').format('YYYY-MM-DD'), today.endOf('day').format('YYYY-MM-DD'), organizationId),
                ]);
                if (mounted) {
                    const orderAttentions = normalizeOrderAttentions(orderResponse);
                    setAppointments(isClinic
                        ? orderAttentions
                        : [...normalizeAppointments(appointmentResponse), ...orderAttentions]);
                }
            } catch (loadError) {
                if (mounted) {
                    setAppointments([]);
                    setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar el workflow.');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [loadAppointments, loadOrderAttentions, organizationId, isClinic, refreshKey]);

    const counts = useMemo(() => ({
        all: appointments.length,
        pending: appointments.filter(item => appointmentStatusGroup(item.status) === 'PENDING').length,
        inConsultation: appointments.filter(item => appointmentStatusGroup(item.status) === 'IN_CONSULTATION').length,
        attended: appointments.filter(item => appointmentStatusGroup(item.status) === 'ATTENDED').length,
    }), [appointments]);

    const filteredAppointments = useMemo(() => appointments.filter(item => filter === 'ALL' || appointmentStatusGroup(item.status) === filter), [appointments, filter]);

    if (loading) return <div className={styles.loading}><Spin /></div>;

    if (error) return <Alert type="warning" showIcon message="Workflow no disponible" description={error} action={<Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(value => value + 1)}>Reintentar</Button>} />;

    return <div className={styles.workflowLayout}>
        <Card className={styles.workflowQueue}>
            <div className={styles.workflowHeader}>
                <div>
                    <Title level={4}>Workflow de atención</Title>
                    <Text type="secondary">Seguimiento de citas y órdenes de atención de {organizationName || 'la organización'}</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(value => value + 1)}>Actualizar</Button>
                    <Badge count={appointments.length} showZero />
                </Space>
            </div>
            <Segmented
                block
                value={filter}
                onChange={(value) => setFilter(value as typeof filter)}
                options={[
                    { label: `Todos ${counts.all}`, value: 'ALL' },
                    { label: `Pendientes ${counts.pending}`, value: 'PENDING' },
                    { label: `En consulta ${counts.inConsultation}`, value: 'IN_CONSULTATION' },
                    { label: `Atendidos ${counts.attended}`, value: 'ATTENDED' },
                ]}
            />
            <div className={styles.workflowList}>
                {filteredAppointments.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay registros para este filtro" /> : filteredAppointments.map((appointment) => <button
                    type="button"
                    key={appointment.id || `${appointment.dni}-${appointment.hour}`}
                    className={`${styles.workflowAppointment} ${selectedAppointment?.id === appointment.id ? styles.workflowAppointmentSelected : ''}`}
                    onClick={() => setSelectedAppointment(appointment)}
                >
                    <div className={styles.appointmentAvatar}>{(appointment.name || appointment.dni || 'P').replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ]/g, '').slice(0, 2).toUpperCase()}</div>
                    <div className={styles.appointmentMain}>
                        <Text strong>{appointment.name || 'Paciente sin nombre'}</Text>
                        <Text type="secondary">{appointment.specialityName || appointment.serviceType || 'Consulta'}{appointment.type ? ` · ${appointment.type}` : ''}</Text>
                        <Text type="secondary">{appointmentDateLabel(appointment.date)}</Text>
                    </div>
                    <div className={styles.appointmentMeta}>
                        <Tag color={appointmentStatusGroup(appointment.status) === 'IN_CONSULTATION' ? 'processing' : appointmentStatusGroup(appointment.status) === 'ATTENDED' ? 'success' : 'default'}>{appointmentStatusLabel(appointment.status)}</Tag>
                        <Text strong>{appointmentHour(appointment.hour)}</Text>
                    </div>
                </button>)}
            </div>
        </Card>

        <Card className={styles.workflowPatient}>
            {!selectedAppointment ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Selecciona una cita para ver el detalle" /> : <div className={styles.workflowPatientContent}>
                <Text type="secondary">Cita seleccionada</Text>
                <div className={styles.workflowPatientHeading}>
                    <div className={styles.workflowPatientAvatar}>{(selectedAppointment.name || selectedAppointment.dni || 'P').replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ]/g, '').slice(0, 2).toUpperCase()}</div>
                    <div>
                        <Title level={4}>{selectedAppointment.name || 'Paciente sin nombre'}</Title>
                        <Text type="secondary">{selectedAppointment.specialityName || selectedAppointment.serviceType || 'Consulta'}</Text>
                    </div>
                </div>
                <div className={styles.workflowField}>
                    <Text type="secondary">Estado</Text>
                    <Tag color={appointmentStatusGroup(selectedAppointment.status) === 'IN_CONSULTATION' ? 'processing' : appointmentStatusGroup(selectedAppointment.status) === 'ATTENDED' ? 'success' : 'default'}>{appointmentStatusLabel(selectedAppointment.status)}</Tag>
                </div>
                {selectedAppointment.origin && <div className={styles.workflowField}>
                    <Text type="secondary">Origen</Text>
                    <Tag color="blue">{selectedAppointment.origin}</Tag>
                </div>}
                <div className={styles.workflowField}>
                    <Text type="secondary">Fecha y hora</Text>
                    <Text strong>{appointmentDateLabel(selectedAppointment.date)} · {appointmentHour(selectedAppointment.hour)}</Text>
                </div>
                <div className={styles.workflowField}>
                    <Text type="secondary">Centro y médico</Text>
                    <Text>{selectedAppointment.centerName || 'Centro no registrado'}</Text>
                    <Text type="secondary">{selectedAppointment.userName || 'Médico no registrado'}</Text>
                </div>
                <div className={styles.workflowField}>
                    <Text type="secondary">Contacto</Text>
                    <Text>{selectedAppointment.phone || 'No registrado'}</Text>
                    <Text type="secondary">{selectedAppointment.email || 'Sin correo'}</Text>
                </div>
            </div>}
        </Card>
    </div>;
};

export default connect(null, { getAppointmentsByOrganization, getMedicalRecordOrganizationOrderAttentions })(WorkflowManagement);
